import React, { useState } from 'react';
import { Product, ProductSize, CreateOrderDto, CreateOrderItemDto } from '@/domain/entities';
import { ProductSelector } from './ProductSelector';

interface CreateOrderFormProps {
  onClose: () => void;
  onSubmit: (orderData: CreateOrderDto) => Promise<void>;
}

const CONTACT_TYPES = [
  { value: 'telegram', label: 'Telegram', placeholder: '@username' },
  { value: 'whatsapp', label: 'WhatsApp', placeholder: '+7 (999) 123-45-67' },
];

const DELIVERY_TYPES = [
  { value: 'pickup', label: 'Самовывоз' },
  { value: 'courier', label: 'Курьер' },
  { value: 'post', label: 'Почта' },
];

const PAYMENT_METHODS = [
  { value: 'cash', label: 'Наличные' },
  { value: 'card', label: 'Карта' },
  { value: 'transfer', label: 'Перевод' },
];

export const CreateOrderForm: React.FC<CreateOrderFormProps> = ({
  onClose,
  onSubmit,
}) => {
  const [formData, setFormData] = useState<CreateOrderDto>({
    customer: {
      name: '',
      phone: '',
      contactType: 'telegram',
    },
    delivery: {
      type: 'pickup',
      address: '',
    },
    items: [],
    comment: '',
    paymentMethod: 'cash',
  });
  
  const [contactValue, setContactValue] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showProductSelector, setShowProductSelector] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    // Валидация
    if (!formData.customer.name.trim()) {
      setError('Укажите имя покупателя');
      return;
    }
    if (!contactValue.trim()) {
      setError('Укажите контакт покупателя');
      return;
    }
    if (formData.items.length === 0) {
      setError('Добавьте хотя бы один товар в заказ');
      return;
    }
    if (formData.delivery.type !== 'pickup' && !formData.delivery.address?.trim()) {
      setError('Укажите адрес доставки');
      return;
    }

    setSubmitting(true);
    try {
      // Очищаем данные перед отправкой
      const cleanData: CreateOrderDto = {
        customer: {
          name: formData.customer.name,
          phone: contactValue, // Используем значение контакта
          contactType: formData.customer.contactType,
        },
        delivery: {
          type: formData.delivery.type,
          address: formData.delivery.type !== 'pickup' ? formData.delivery.address : undefined,
        },
        items: formData.items.map(item => ({
          sku: item.sku,
          usSize: item.usSize,
          quantity: item.quantity,
        })),
        comment: formData.comment || undefined,
        paymentMethod: formData.paymentMethod || undefined,
      };
      
      await onSubmit(cleanData);
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Не удалось создать заказ');
    } finally {
      setSubmitting(false);
    }
  };

  const handleSelectProduct = (product: Product, size: ProductSize) => {
    const newItem: CreateOrderItemDto = {
      sku: product.sku,
      usSize: size.us,
      quantity: 1,
      // Дополнительные поля для UI
      productName: product.name,
      price: size.price,
      imageUrl: product.imageUrl,
    };

    setFormData((prev) => ({
      ...prev,
      items: [...prev.items, newItem],
    }));

    setShowProductSelector(false);
    setError(null);
  };

  const handleRemoveItem = (index: number) => {
    setFormData((prev) => ({
      ...prev,
      items: prev.items.filter((_, i) => i !== index),
    }));
  };

  const handleUpdateQuantity = (index: number, newQuantity: number) => {
    if (newQuantity < 1) return;
    setFormData((prev) => ({
      ...prev,
      items: prev.items.map((item, i) => 
        i === index ? { ...item, quantity: newQuantity } : item
      ),
    }));
  };

  const calculateTotal = () => {
    return formData.items.reduce((sum, item) => sum + (item.price || 0) * item.quantity, 0);
  };

  return (
    <div className="fixed inset-0 flex items-center justify-center z-50 px-4 py-8 bg-black/50" onClick={onClose}>
      <div
        className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden border border-gray-200 dark:border-gray-700"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex justify-between items-center px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Создать заказ вручную</h2>
          <button onClick={onClose} className="text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="flex flex-col h-full">
          {error && (
            <div className="mx-6 mt-4 mb-2 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-400 px-4 py-3 rounded">
              {error}
            </div>
          )}

          <div className="flex-1 overflow-y-auto">
            <div className="grid grid-cols-2 gap-6 p-6">
              {/* Левая колонка - форма */}
              <div className="space-y-4">
            {/* Имя покупателя */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Имя покупателя <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={formData.customer.name}
                onChange={(e) => setFormData(prev => ({ ...prev, customer: { ...prev.customer, name: e.target.value }}))}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                placeholder="Введите имя"
                required
              />
            </div>

            {/* Тип контакта */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Тип контакта <span className="text-red-500">*</span>
              </label>
              <select
                value={formData.customer.contactType}
                onChange={(e) => {
                  setFormData(prev => ({ ...prev, customer: { ...prev.customer, contactType: e.target.value }}));
                  setContactValue(''); // Очищаем контакт при смене типа
                }}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
              >
                {CONTACT_TYPES.map((type) => (
                  <option key={type.value} value={type.value}>
                    {type.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Контакт */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                {formData.customer.contactType === 'telegram' ? 'Telegram' : 'WhatsApp'} <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={contactValue}
                onChange={(e) => setContactValue(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                placeholder={CONTACT_TYPES.find(t => t.value === formData.customer.contactType)?.placeholder}
                required
              />
            </div>

            {/* Тип доставки */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Тип доставки
              </label>
              <select
                value={formData.delivery.type}
                onChange={(e) => setFormData(prev => ({ ...prev, delivery: { ...prev.delivery, type: e.target.value }}))}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
              >
                {DELIVERY_TYPES.map((type) => (
                  <option key={type.value} value={type.value}>
                    {type.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Адрес доставки */}
            {formData.delivery.type !== 'pickup' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Адрес доставки <span className="text-red-500">*</span>
                </label>
                <textarea
                  value={formData.delivery.address || ''}
                  onChange={(e) => setFormData(prev => ({ ...prev, delivery: { ...prev.delivery, address: e.target.value }}))}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  placeholder="Введите адрес доставки"
                  rows={2}
                />
              </div>
            )}

            {/* Способ оплаты */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Способ оплаты
              </label>
              <select
                value={formData.paymentMethod || 'cash'}
                onChange={(e) => setFormData(prev => ({ ...prev, paymentMethod: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
              >
                {PAYMENT_METHODS.map((method) => (
                  <option key={method.value} value={method.value}>
                    {method.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Комментарий */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Комментарий
              </label>
              <textarea
                value={formData.comment || ''}
                onChange={(e) => setFormData(prev => ({ ...prev, comment: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                placeholder="Добавьте комментарий к заказу"
                rows={3}
              />
            </div>
          </div>

          {/* Правая колонка - товары */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
              Товары в заказе <span className="text-red-500">*</span>
            </h3>

            {/* Список добавленных товаров */}
            {formData.items.length > 0 && (
              <div className="space-y-2">
                  {formData.items.map((item, index) => (
                    <div
                      key={index}
                      className="flex items-center gap-3 p-3 bg-gray-50 dark:bg-gray-700 rounded-md border border-gray-200 dark:border-gray-600"
                    >
                      {item.imageUrl && (
                        <img
                          src={item.imageUrl}
                          alt={item.productName}
                          className="w-12 h-12 object-cover rounded"
                          onError={(e) => {
                            e.currentTarget.style.display = 'none';
                          }}
                        />
                      )}
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium text-gray-900 dark:text-white truncate">
                          {item.productName}
                        </div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">
                          SKU: {item.sku} • Размер: {item.usSize}
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          onClick={() => handleUpdateQuantity(index, item.quantity - 1)}
                          className="w-7 h-7 flex items-center justify-center rounded bg-gray-200 dark:bg-gray-600 hover:bg-gray-300 dark:hover:bg-gray-500 text-gray-700 dark:text-gray-200"
                        >
                          -
                        </button>
                        <span className="w-8 text-center text-sm font-medium text-gray-900 dark:text-white">
                          {item.quantity}
                        </span>
                        <button
                          type="button"
                          onClick={() => handleUpdateQuantity(index, item.quantity + 1)}
                          className="w-7 h-7 flex items-center justify-center rounded bg-gray-200 dark:bg-gray-600 hover:bg-gray-300 dark:hover:bg-gray-500 text-gray-700 dark:text-gray-200"
                        >
                          +
                        </button>
                      </div>
                      <div className="text-sm font-semibold text-gray-900 dark:text-white min-w-[80px] text-right">
                        {(item.quantity * item.price).toLocaleString('ru-RU')} ₽
                      </div>
                      <button
                        type="button"
                        onClick={() => handleRemoveItem(index)}
                        className="text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>
                  ))}
                <div className="flex justify-between items-center p-3 bg-indigo-50 dark:bg-indigo-900/30 rounded-md border border-indigo-200 dark:border-indigo-700">
                  <div className="text-sm font-semibold text-gray-900 dark:text-white">
                    Итого:
                  </div>
                  <div className="text-lg font-bold text-indigo-600 dark:text-indigo-400">
                    {calculateTotal().toLocaleString('ru-RU')} ₽
                  </div>
                </div>
              </div>
            )}

            {/* Выбор товара из каталога */}
            {showProductSelector ? (
              <div className="border border-indigo-300 dark:border-indigo-700 rounded-md p-4 bg-indigo-50/50 dark:bg-indigo-900/10">
                <div className="flex justify-between items-center mb-3">
                  <h4 className="text-sm font-semibold text-gray-900 dark:text-white">
                    Выберите товар из каталога
                  </h4>
                  <button
                    type="button"
                    onClick={() => setShowProductSelector(false)}
                    className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
                <ProductSelector onSelectProduct={handleSelectProduct} />
              </div>
            ) : (
              <button
                type="button"
                onClick={() => setShowProductSelector(true)}
                className="w-full px-4 py-3 bg-indigo-600 hover:bg-indigo-700 text-white rounded-md font-medium transition-colors flex items-center justify-center gap-2"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" />
                </svg>
                Добавить товар из каталога
              </button>
            )}
          </div>
        </div>
      </div>

      <div className="border-t border-gray-200 dark:border-gray-700 px-6 py-4 flex justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            className="px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Отмена
          </button>
          <button
            type="submit"
            disabled={submitting}
            className="px-6 py-2 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-sm font-medium text-white disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {submitting ? 'Создание...' : 'Создать заказ'}
          </button>
        </div>
      </form>
      </div>
    </div>
  );
};

